
import React from 'react';
import SectionHeading from '../ui/SectionHeading';
import { FileSearch, Target, Cog, TrendingUp, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import GlossaryAutoLinker from '../glossary/GlossaryAutoLinker';

interface ProcessStepDetailedProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  whatWeDo: string;
  deliverables: string[];
  successLooksLike: string;
  isLast?: boolean;
  enableGlossary?: boolean;
}

const ProcessStepDetailed: React.FC<ProcessStepDetailedProps> = ({
  number,
  icon,
  title,
  whatWeDo,
  deliverables,
  successLooksLike,
  isLast = false,
  enableGlossary = false
}) => {
  return (
    <div className="flex">
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brand-teal to-brand-purple flex items-center justify-center text-white font-medium shrink-0">
          {number}
        </div>
        {!isLast && (
          <div className="w-0.5 h-full bg-gray-200 mt-4 relative">
            <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-brand-teal/50 to-transparent"></div>
          </div>
        )}
      </div>
      <div className={cn("ml-6 pb-10", isLast && "pb-0")}>
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="mr-3 text-brand-teal">
            {icon}
          </div>
          <h3 className="text-xl font-ibm font-semibold">{title}</h3>
        </div>
        
        {/* What We Do */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-brand-teal uppercase tracking-wide mb-2">What we do</h4>
          {enableGlossary ? (
            <GlossaryAutoLinker><p className="text-gray-600">{whatWeDo}</p></GlossaryAutoLinker>
          ) : (
            <p className="text-gray-600">{whatWeDo}</p>
          )}
        </div>
        
        {/* Deliverables */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-brand-teal uppercase tracking-wide mb-2">What you get</h4>
          {enableGlossary ? (
            <GlossaryAutoLinker>
              <ul className="space-y-1">
                {deliverables.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-600">
                    <CheckCircle2 size={16} className="text-brand-teal mr-2 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </GlossaryAutoLinker>
          ) : (
            <ul className="space-y-1">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start text-gray-600">
                  <CheckCircle2 size={16} className="text-brand-teal mr-2 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {/* Success Looks Like */}
        <div className="bg-gray-50 border-l-3 border-brand-teal pl-4 py-2 rounded-r">
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Success looks like</h4>
          <p className="text-gray-600 text-sm">{successLooksLike}</p>
        </div>
      </div>
    </div>
  );
};

const ProcessSection: React.FC = () => {
  const steps = [
    {
      number: 1,
      icon: <FileSearch size={24} />,
      title: "Discovery & AI Readiness Evaluation",
      whatWeDo: "We conduct an AI Readiness Evaluation of your current workflows, identify pain points and opportunities, and capture baseline metrics. This includes understanding what's working well—sometimes the smartest first step is protecting stable processes.",
      deliverables: [
        "AI Readiness Report with clear recommendations",
        "Baseline metrics document (current time, cost, error rates)",
        "Prioritised opportunity list ranked by impact and feasibility"
      ],
      successLooksLike: "A clear, shared understanding of where AI can add genuine value—and where it shouldn't."
    },
    {
      number: 2,
      icon: <Target size={24} />,
      title: "AI Workflow Intelligence Strategy & Use-Case Prioritisation",
      whatWeDo: "We develop a custom AI roadmap tailored to your business, prioritise use-cases by ROI and risk, and design human-in-the-loop oversight from the start. Every recommendation is justifiable, reversible, proportionate, and defensible.",
      deliverables: [
        "AI Strategy Document with phased roadmap",
        "Use-case priority matrix (ROI vs complexity)",
        "ROI projections with measurable success criteria",
        "Governance framework with human oversight design"
      ],
      successLooksLike: "A clear, agreed roadmap with measurable outcomes defined—no surprises, no scope creep."
    },
    {
      number: 3,
      icon: <Cog size={24} />,
      title: "Guided Implementation",
      whatWeDo: "We build and integrate workflow intelligence solutions into your existing workflows, provide hands-on staff training, and deploy with minimal disruption. Every deployment includes a rollback plan and maintains human oversight throughout.",
      deliverables: [
        "Working AI solution(s) integrated into your systems",
        "Staff training and onboarding completed",
        "Rollback plan and contingency procedures",
        "Initial performance benchmarks captured"
      ],
      successLooksLike: "Initial AI use-cases live and operational, with staff confident using the new tools."
    },
    {
      number: 4,
      icon: <TrendingUp size={24} />,
      title: "Ongoing Support, Governance & Optimisation",
      whatWeDo: "We monitor performance, optimise outputs based on real-world usage, ensure ongoing compliance, and provide structured 30/60/90 day reviews. You're never left on your own after go-live.",
      deliverables: [
        "Monthly performance reports with ROI tracking",
        "30/60/90 day optimisation reviews",
        "Governance documentation and compliance oversight",
        "Priority support for issues and adjustments"
      ],
      successLooksLike: "Measurable improvements against baseline, continuous optimisation, and long-term sustainable value."
    },
  ];
  
  return (
    <div id="process-section" className="section bg-gray-50">
      <div className="section-inner">
        <SectionHeading 
          title="How It Works – Our Workflow Intelligence Adoption Method"
          subtitle="A responsible, human-centred approach to AI adoption aligned with New Zealand's AI strategy. Clear scope, measurable outcomes, and ongoing support."
        />
        
        <div className="md:grid md:grid-cols-12 mt-12">
          <div className="md:col-span-1 lg:col-span-2"></div>
          <div className="md:col-span-10 lg:col-span-8">
            {steps.map((step, index) => (
              <ProcessStepDetailed
                key={index}
                number={step.number}
                icon={step.icon}
                title={step.title}
                whatWeDo={step.whatWeDo}
                deliverables={step.deliverables}
                successLooksLike={step.successLooksLike}
                isLast={index === steps.length - 1}
                enableGlossary={index <= 1}
              />
            ))}
          </div>
          <div className="md:col-span-1 lg:col-span-2"></div>
        </div>
      </div>
    </div>
  );
};

export default ProcessSection;
